import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';

// Pages
import RoleSelectPage from './pages/RoleSelectPage';
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';
import ChildHomePage from './pages/ChildHomePage';
import ChildPermissionsPage from './pages/ChildPermissionsPage';
import ChildChatPage from './pages/ChildChatPage';
import ChildHelpPage from './pages/ChildHelpPage';
import ChildSettingsPage from './pages/ChildSettingsPage';
import DashboardPage from './pages/DashboardPage';
import LocationPage from './pages/LocationPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ControlPage from './pages/ControlPage';
import SOSPage from './pages/SOSPage';
import LogsPage from './pages/LogsPage';
import SettingsPage from './pages/SettingsPage';

// Layout
import DashboardLayout from './components/DashboardLayout';
import ChildLayout from './components/ChildLayout';
import SOSBanner from './components/SOSBanner';
import ConnectionStatus from './components/ConnectionStatus';
import ChildAccessRequestModal from './components/ChildAccessRequestModal';
import LoadingPage from './components/LoadingPage';

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#1a2236',
          color: '#f0f4ff',
          fontFamily: 'Inter, sans-serif',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div>
            <h1 style={{ fontSize: '24px', marginBottom: '10px' }}>⚠️ Error Loading Dashboard</h1>
            <p style={{ marginBottom: '10px' }}>{this.state.errorMessage}</p>
            <p style={{ fontSize: '12px', color: '#ccc', marginBottom: '20px' }}>Check browser console (F12) for details</p>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}>
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function PrivateRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingPage />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === 'child' ? '/child' : '/dashboard'} replace />;
  }
  return children;
}

const getRoleHomePath = (user) => (user?.role === 'child' ? '/child' : '/dashboard');

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) return <LoadingPage />;

  return (
    <BrowserRouter>
      <SocketProvider>
        <ConnectionStatus />
        <ChildAccessRequestModal />
        <SOSBanner />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#1a2236',
              color: '#f0f4ff',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px'
            },
            success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
            error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } }
          }}
        />
        <Routes>
          <Route path="/" element={!user ? <RoleSelectPage /> : <Navigate to={getRoleHomePath(user)} />} />
          <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={getRoleHomePath(user)} />} />

          {/* Parent Dashboard Routes */}
          <Route path="/dashboard" element={
            <PrivateRoute role="parent">
              <DashboardLayout />
            </PrivateRoute>
          }>
            <Route index element={<DashboardPage />} />
            <Route path="location" element={<LocationPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="control" element={<ControlPage />} />
            <Route path="sos" element={<SOSPage />} />
            <Route path="logs" element={<LogsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Child App Routes */}
          <Route path="/child" element={
            <PrivateRoute role="child">
              <ChildLayout />
            </PrivateRoute>
          }>
            <Route index element={<ChildHomePage />} />
            <Route path="permissions" element={<ChildPermissionsPage />} />
            <Route path="chat" element={<ChildChatPage />} />
            <Route path="help" element={<ChildHelpPage />} />
            <Route path="settings" element={<ChildSettingsPage />} />
          </Route>

          <Route path="*" element={<Navigate to={user ? getRoleHomePath(user) : '/'} />} />
        </Routes>
      </SocketProvider>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}
